import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { selectSubscriptionList } from 'root-redux/selects/common'
import { OptionType, Option } from 'components/Option'
import {
  selectCurrency,
  selectProductId,
  selectSubscriptionFullPrice,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionPriceId,
} from 'modules/purchase/redux/selects'
import { setSelectedSubscriptionAction } from 'modules/purchase/redux/actions'
import { ISubscription } from 'models/subscriptions.model'
import { SubscriptionItem } from 'modules/purchase/components/SubscriptionItem'
import { CURRENCY_SYMBOLS } from 'modules/purchase/constants'
import { PRICE_DESCRIPTION_KEYS } from 'modules/purchase/components/SubscriptionsBlock/constants'
import { TermsOfUseLink } from 'components/TermsOfUseLink'
import { StyledSubscriptionsBlock as S } from './SubscriptionsBlock.styles'

type TProps = {
  elemForComparisonRef: React.RefObject<HTMLDivElement>
  onButtonClick: () => void
}

export const SubscriptionsBlock: React.FC<TProps> = ({
  onButtonClick,
  elemForComparisonRef,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const subscriptions = useSelector(selectSubscriptionList)
  const selectedSubscriptionPriceId = useSelector(selectSubscriptionPriceId)
  const productId = useSelector(selectProductId)
  const currency = useSelector(selectCurrency)
  const fullPrice = useSelector(selectSubscriptionFullPrice)
  const periodQuantity = useSelector(selectSubscriptionPeriodQuantity)

  const defaultSubscription = subscriptions.find(({ isDefault }) => isDefault)
  const selectedSubscriptionInstance = subscriptions.filter(
    (subscription) => subscription.priceId === selectedSubscriptionPriceId,
  )[0]
  const [selectedSubscription, setSelectedSubscription] =
    useState<ISubscription | null>(
      selectedSubscriptionInstance || defaultSubscription,
    )

  useEffect(() => {
    dispatch(
      setSelectedSubscriptionAction(selectedSubscription as ISubscription),
    )
  }, [dispatch, selectedSubscription])

  const handleChange = useCallback(
    (value) => {
      const checkedSubscription = subscriptions.find(
        (subscription) => subscription.priceId === value,
      )
      setSelectedSubscription(checkedSubscription as ISubscription)
    },
    [subscriptions],
  )

  return (
    <S.Wrapper>
      <S.Title>{t`purchase.subscription.title`}</S.Title>
      {subscriptions.map((subscription) => (
        <Option
          type={OptionType.RADIO}
          name="subscription"
          value={subscription.priceId}
          key={subscription.priceId}
          onChange={handleChange}
          withoutMargin
        >
          <SubscriptionItem
            subscription={subscription}
            isSelected={subscription.priceId === selectedSubscriptionPriceId}
          />
        </Option>
      ))}
      <S.ButtonContainer ref={elemForComparisonRef}>
        <S.Button type="button" onClick={onButtonClick}>
          {t`actions.startNow`}
        </S.Button>
      </S.ButtonContainer>
      <S.Disclaimer>
        <Trans
          i18nKey={PRICE_DESCRIPTION_KEYS[productId] as any}
          values={{
            periodQuantity,
            price: `${CURRENCY_SYMBOLS[currency]}${fullPrice}`,
          }}
        />
        <TermsOfUseLink /> {t('purchase.subscription.contactUs')}
        <a href="mailto:support@harmonybit.com">support@harmonybit.com</a>
      </S.Disclaimer>
    </S.Wrapper>
  )
}
